import { useToast } from '@/components/ui/Toast'
import { fetchCSRFToken } from '@/lib/api/client'
import { queryKeys } from '@/lib/queryClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useEffect } from 'react'
import { authApi } from '../api/authApi'

const AuthContext = createContext(null)

/**
 * Auth Provider Component
 * Manages authentication state and provides auth methods
 */
export function AuthProvider({ children }) {
  const queryClient = useQueryClient()
  const toast = useToast()

  // Fetch CSRF token on mount to ensure it's available for login
  useEffect(() => {
    fetchCSRFToken()
  }, [])

  // Query current user
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: authApi.me,
    retry: false,
    staleTime: Infinity, // User data doesn't go stale
  })

  // Extract user from API response: {success, data: {...user...}}
  const user = data?.data

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (response) => {
      // Backend returns {success: true, data: {user: {...}, csrfToken: '...'}}
      // or {success: true, data: {...user...}}
      const userData = response?.data?.user || response?.data
      queryClient.setQueryData(queryKeys.auth.me, response)
      // Refetch to ensure we have the session
      refetch()
      toast.success({
        title: 'Welcome back!',
        description: 'You have successfully logged in.',
      })
    },
    onError: (error) => {
      // Don't show toast if there are field errors (they'll be shown under inputs)
      if (!error.error?.details) {
        toast.error({
          title: 'Login failed',
          description: error.message || 'Invalid credentials. Please try again.',
        })
      }
      // Re-throw error so Login component can display field errors
      throw error
    },
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.auth.me, null)
      queryClient.clear() // Clear all cached queries
      toast.success({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
      })
    },
    onError: () => {
      // Even if logout API fails, clear local state
      queryClient.setQueryData(queryKeys.auth.me, null)
      queryClient.clear()
    },
  })

  // React 19 compiler automatically optimizes - no need for useCallback
  async function login(credentials) {
    return loginMutation.mutateAsync(credentials)
  }

  async function logout() {
    return logoutMutation.mutateAsync()
  }

  function refreshUser() {
    return refetch()
  }

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={ value }>{ children }</AuthContext.Provider>
}

/**
 * useAuth Hook
 * Access authentication state and methods
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export default AuthContext
